import React, { useState, useEffect } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { forceReloadContactDocuments } from '@hooks/API/Contact/UseContactMedia';

import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import { NwTag } from '@components/Gui/NwTag';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const VideoCoverContainer = styled.div`
    padding: 0.5rem 0 1rem 0;

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 2rem;
    }

    .empty-state {
        text-align: center;
        padding: 2rem;
        color: ${(props) => props.theme.TextSecondary};
    }

    .thumbnails-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 1rem;
        margin-top: 1rem;
    }

    .thumbnail-wrapper {
        position: relative;
        cursor: pointer;
        border-radius: 8px;
        overflow: hidden;
        border: 3px solid transparent;
        transition: all 0.2s ease-in-out;
        flex: 0 0 calc((100% - 5rem) / 6);
        max-width: calc((100% - 5rem) / 6);
        aspect-ratio: 4 / 3;

        @media (max-width: 1600px) {
            flex: 0 0 calc((100% - 2rem) / 3);
            max-width: calc((100% - 2rem) / 3);
        }

        &.active {
            border-color: ${(props) => props.theme.PrimaryColor};
        }

        &:hover {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }

        .active-badge {
            position: absolute;
            bottom: 0.15rem;
            left: 0.15rem;
        }
    }
`;

const VideoCoverSelector = ({ documentID, contactID, onCoverUpdate }) => {
    const [covers, setCovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        fetchCovers();
    }, [documentID]);

    const fetchCovers = async () => {
        try {
            setLoading(true);
            const response = await Axios.get(`/documents/GetVideoCovers/${documentID}`);
            setCovers(response || []);
        } catch (error) {
            console.error('Error fetching video covers:', error);
            setCovers([]);
        } finally {
            setLoading(false);
        }
    };

    const getActiveCover = () => {
        return covers.find((cover) => cover.Type === 'Cover');
    };

    const handleSelectCover = async (mediaID) => {
        const activeCover = getActiveCover();
        if (updating || (activeCover && mediaID === activeCover.MediaID)) {
            return;
        }

        try {
            setUpdating(true);
            await Axios.put(`/documents/SetVideoCover/${documentID}/${mediaID}`);

            // Update local state
            setCovers(
                covers.map((cover) => ({
                    ...cover,
                    Type: cover.MediaID === mediaID ? 'Cover' : 'AlternativeCover',
                }))
            );

            // Reload contact documents
            if (contactID) {
                forceReloadContactDocuments(contactID);
                forceReloadContact(contactID);
            }

            // Notify parent component to reload
            if (onCoverUpdate) {
                onCoverUpdate();
            }
        } catch (error) {
            console.error('Error setting video cover:', error);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <NwCard icon={byPrefixAndName.fasl['image']} title="Video Cover (click to select)">
            <VideoCoverContainer>
                {loading ? (
                    <div className="loading-container">
                        <Loading />
                    </div>
                ) : covers.length === 0 ? (
                    <div className="empty-state">No covers available for this video</div>
                ) : (
                    <div className="thumbnails-container">
                        {covers.map((cover) => {
                            const isActive = cover.Type === 'Cover';
                            return (
                                <div
                                    key={cover.MediaID}
                                    className={`thumbnail-wrapper${isActive ? ' active' : ''}`}
                                    onClick={() => handleSelectCover(cover.MediaID)}
                                >
                                    <img src={cover.Url} alt="Video cover" />
                                    {isActive && (
                                        <div className="active-badge">
                                            <NwTag type="primary" uppercase>
                                                Active
                                            </NwTag>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </VideoCoverContainer>
        </NwCard>
    );
};

export default VideoCoverSelector;
